/**
 * SEO metadata per route. Used for document title and meta description.
 */

const SITE_NAME = 'RideSpot';
const DEFAULT_DESCRIPTION =
  'Share and discover car spots. Post photos of cars you see, build your garage, and connect with car enthusiasts.';
const INDEX_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const NOINDEX_ROBOTS = 'noindex, follow';
const SITE_URL_ENV = process.env.REACT_APP_SITE_URL;

export interface RouteMeta {
  title: string;
  description: string;
  robots?: string;
}

/** Full page title for document (e.g. "Recent spots | RideSpot"). */
export function getPageTitle(title: string): string {
  return title ? `${title} | ${SITE_NAME}` : SITE_NAME;
}

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

function getSiteBaseUrl(fallbackOrigin?: string): string {
  const raw = SITE_URL_ENV?.trim();
  if (raw) {
    try {
      const siteUrl = new URL(raw);
      const basePath = siteUrl.pathname.replace(/\/+$/, '');
      return `${siteUrl.origin}${basePath}`;
    } catch {
      // Ignore invalid URLs and fall back to the current origin.
    }
  }

  return (fallbackOrigin ?? '').replace(/\/+$/, '');
}

export function getCanonicalUrl(pathname: string, fallbackOrigin?: string): string {
  const path = normalizePathname(pathname);
  const baseUrl = getSiteBaseUrl(fallbackOrigin);
  if (!baseUrl) return path;
  return path === '/' ? baseUrl : `${baseUrl}${path}`;
}

/** Get meta for a pathname. Returns default meta for unknown routes. */
export function getMetaForPath(pathname: string): RouteMeta {
  const path = normalizePathname(pathname);
  const meta = ROUTE_META[path];
  if (meta) return meta;
  // Dynamic routes: public content can be indexed; account and edit utilities stay out of search.
  if (path.startsWith('/spot/') && !path.includes('/edit')) {
    return { title: 'Spot', description: DEFAULT_DESCRIPTION, robots: INDEX_ROBOTS };
  }
  if (path.startsWith('/spot/') && path.includes('/edit')) {
    return { title: 'Edit spot', description: DEFAULT_DESCRIPTION, robots: NOINDEX_ROBOTS };
  }
  if (path === '/garage/new' || path.endsWith('/edit')) {
    return { title: 'Edit garage', description: DEFAULT_DESCRIPTION, robots: NOINDEX_ROBOTS };
  }
  if (path.startsWith('/garage/')) {
    return { title: 'Garage car', description: DEFAULT_DESCRIPTION, robots: INDEX_ROBOTS };
  }
  if (path.startsWith('/garages/user/')) {
    return { title: 'Garage', description: DEFAULT_DESCRIPTION, robots: NOINDEX_ROBOTS };
  }
  if (path.startsWith('/garages/')) {
    return { title: 'Garage', description: DEFAULT_DESCRIPTION, robots: INDEX_ROBOTS };
  }
  if (path.startsWith('/user/')) {
    return { title: 'Profile', description: DEFAULT_DESCRIPTION, robots: INDEX_ROBOTS };
  }
  return { title: SITE_NAME, description: DEFAULT_DESCRIPTION, robots: INDEX_ROBOTS };
}

const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: 'Car spotting, garages, and build stories',
    description: 'Discover car spots, public garages, and standout contributors on RideSpot. Join the community to share sightings, builds, and car culture.',
    robots: INDEX_ROBOTS,
  },
  '/spot': {
    title: 'Spot a car',
    description: 'Post a photo of a car you spotted. Add make, model, location, and share with the community.',
    robots: NOINDEX_ROBOTS,
  },
  '/search': {
    title: 'Search spots',
    description: 'Find car spots by make and model. Search the RideSpot community for your favorite cars.',
    robots: INDEX_ROBOTS,
  },
  '/about': {
    title: 'About',
    description: 'Learn about RideSpot and its main features, including spots, member garages, and community leaderboards.',
    robots: INDEX_ROBOTS,
  },
  '/feed': {
    title: 'RideSpot feed',
    description: 'Browse the full RideSpot feed with the latest community car sightings, newest first.',
    robots: INDEX_ROBOTS,
  },
  '/garage': {
    title: 'My garage',
    description: 'Manage your garage. Add cars you own or have owned, track mods and build progress.',
    robots: NOINDEX_ROBOTS,
  },
  '/garages/featured': {
    title: 'Featured garages',
    description: 'Browse featured car garages from the RideSpot community. Discover builds and collections.',
    robots: INDEX_ROBOTS,
  },
  '/garages/search': {
    title: 'Search garages',
    description: 'Search garages by owner name, make, or model. Find cars and builds on RideSpot.',
    robots: INDEX_ROBOTS,
  },
  '/login': {
    title: 'Sign in',
    description: 'Sign in to your RideSpot account to post spots, manage your garage, and more.',
    robots: NOINDEX_ROBOTS,
  },
  '/create-account': {
    title: 'Create account',
    description: 'Create a RideSpot account to share car spots, build your garage, and join the community.',
    robots: NOINDEX_ROBOTS,
  },
  '/profile': {
    title: 'My profile',
    description: 'Edit your RideSpot profile, display name, avatar, and account settings.',
    robots: NOINDEX_ROBOTS,
  },
  '/contact': {
    title: 'Contact',
    description: 'Contact the RideSpot team with questions, feature requests, or bug reports.',
    robots: INDEX_ROBOTS,
  },
  '/feedback': {
    title: 'Contact',
    description: 'Contact the RideSpot team with questions, feature requests, or bug reports.',
    robots: INDEX_ROBOTS,
  },
  '/favorites': {
    title: 'Favorites',
    description: 'Browse tagged car photos from across the RideSpot community.',
    robots: INDEX_ROBOTS,
  },
  '/my-favorites': {
    title: 'My favorites',
    description: 'View the RideSpot posts you have personally tagged as liked, beautiful, cool, or sweet photo.',
    robots: NOINDEX_ROBOTS,
  },
  '/badges': {
    title: 'Badges',
    description: 'Earn badges on RideSpot for spotting cars and building your garage. See requirements and tiers.',
    robots: INDEX_ROBOTS,
  },
  '/most-voted': {
    title: 'Most voted',
    description: 'Top car spots by likes, beautiful, cool, and sweet photo reactions. Community favorites.',
    robots: INDEX_ROBOTS,
  },
  '/top-contributors': {
    title: 'All-Time Legends',
    description: 'Top RideSpot contributors ranked by score. See who posts the best car spots.',
    robots: INDEX_ROBOTS,
  },
  '/top-spotters-week': {
    title: "This week's top spotters",
    description: 'RideSpot users whose spots earned the most engagement this week. Weekly leaderboard.',
    robots: INDEX_ROBOTS,
  },
};

export { SITE_NAME, DEFAULT_DESCRIPTION, INDEX_ROBOTS, NOINDEX_ROBOTS };
